import React, { useContext, useState, useEffect, useMemo } from 'react'
import { UserContext, NotifyContext } from 'context'
import { Button, Dropdown, Input, Loader } from 'semantic-ui-react'

import api from '../../../actions/api'
import dateUtils from '../../../utils/dateUtils'

import './BidPage.css'
import { injectIntl } from 'react-intl'
import doRequest from 'actions/doRequest'
import DatePicker from 'react-date-picker'

function BidPage(props) {
    const { intl } = props

    const [loading, setLoading] = useState(true)
    const [sending, setSending] = useState(false)
    const [options, setOptions] = useState({})
    const [values, setValues] = useState({ requestWieght: 1 })
    const [requestType, setRequestType] = useState('unloading')
    const [blockedDays, setBlockedDays] = useState([])

    const { logout } = useContext(UserContext)
    const { pushNotify } = useContext(NotifyContext)

    const translates = useMemo(() => ({
        elevator: intl.formatMessage({ id: "title.elevator" }),
        culture: intl.formatMessage({ id: "title.culture" }),
        requestType: intl.formatMessage({ id: "title.type" }),
        noResult: intl.formatMessage({ id: "title.resultNotFound" }),
        sendRequest: intl.formatMessage({ id: "btn.send" }),
        uploading: intl.formatMessage({ id: 'ordertype.uploading' }),
        unloading: intl.formatMessage({ id: 'ordertype.unloading' }),
        requestDate: intl.formatMessage({ id: 'title.date' }),
        requestWieght: intl.formatMessage({ id: 'title.weight' }),
        tons: intl.formatMessage({ id: 'other.units.tons' }),
        tonsShort: intl.formatMessage({ id: 'other.units.tonsShort' })
    }), [intl.locale]) // eslint-disable-line react-hooks/exhaustive-deps

    const requestTypeOptions = [
        { key: 0, text: translates.unloading, value: 'unloading' },
        { key: 1, text: translates.uploading, value: 'uploading' }
    ]


    const getOptions = () => {
        setLoading(true)
        let requests = []

        requests.push(doRequest(api.elevators.list(), pushNotify, logout).ready)
        requests.push(doRequest(api.cultures.list(), pushNotify, logout).ready)

        Promise.all(requests).then(result => {
            const res = result.reduce((acc, data) => {
                if (data.elevators) return { ...acc, elevators: data.elevators.map((elevator) => { return { key: elevator.id, value: elevator.id, text: elevator.name } }) };
                if (data.cultures) return { ...acc, cultures: data.cultures.map((culture) => { return { key: culture.id, value: culture.id, text: intl.formatMessage({ id: `cultures.${culture.title}` }) } }) };
                return acc;
            }, {})

            setOptions({ ...res })
        }).catch(console.error)
            .finally(() => setLoading(false))
    }

    useEffect(getOptions, []) // eslint-disable-line react-hooks/exhaustive-deps

    const getBlockedDays = () => {
        doRequest(api.blockedDays.list(), pushNotify, logout).ready.then(res => setBlockedDays(res?.blockedDays)).catch(console.error)
    }

    useEffect(getBlockedDays, []) // eslint-disable-line react-hooks/exhaustive-deps

    const isTileDisabled = ({ date }) => {
        return blockedDays.some(day => day === dateUtils.formatDate(date));
    };

    const sendRequest = () => {
        const request = {
            elevator: typeof values.elevator === 'number' ? values.elevator : undefined,
            cultures: typeof values.culture === 'number' ? values.culture : undefined,
            type: requestType,
            scheduledDate: values.requestDate,
            weight: values.requestWieght
        }

        if (!request.elevator) {
            pushNotify('error', 'title.error', 'msg.elevatorMustBeFilled', 2000)
            return
        }
        if (!request.cultures) {
            pushNotify('error', 'title.error', 'msg.clatureMustBeFilled', 2000)
            return
        }

        if (!request.scheduledDate) {
            pushNotify('error', 'title.error', 'msg.dateMustBeFilled', 2000)
            return
        }

        if (!request.weight) {
            pushNotify('error', 'title.error', 'msg.weightMustBeFilled', 2000)
            return
        }

        if (request?.weight < 1 || isNaN(request?.weight)) {
            pushNotify('error', 'title.error', 'msg.weightMustBeGreaterThanZero', 2000)
            return
        }

        setSending(true)
        doRequest(api.applications.addApplication(request), pushNotify, logout).ready.then(res => {
            pushNotify('success', 'title.success', 'msg.sendRequstSuccess', 2000)
            setValues({
                elevator: null,
                culture: null,
                requestWieght: 1
            })
        }).catch(console.error)
            .finally(() => {
                setSending(false)
            })
    }

    return (
        loading ?
            <Loader className={"page-loader"} active inline="centered" />
            :
            <div className="requestForm">
                <div className="requestItem">
                    <div>{translates.elevator}<b>*</b>:</div>
                    <Dropdown
                        search
                        fluid
                        selection={true}
                        placeholder={translates.elevator}
                        options={options.elevators}
                        disabled={sending}
                        value={values.elevator}
                        noResultsMessage={translates.noResult}
                        onChange={(e, { value }) => setValues({ ...values, elevator: value })}
                    />
                </div>
                <div className="requestItem">
                    <div>{translates.culture}<b>*</b>:</div>
                    <Dropdown
                        search
                        fluid
                        selection={true}
                        placeholder={translates.culture}
                        options={options.cultures}
                        disabled={sending}
                        value={values.culture}
                        noResultsMessage={translates.noResult}
                        onChange={(e, { value }) => setValues({ ...values, culture: value })}
                    />
                </div>
                <div className="requestItem">
                    <div>{translates.requestType}:</div>
                    <Dropdown
                        fluid
                        selection={true}
                        options={requestTypeOptions}
                        disabled={sending}
                        value={requestType}
                        noResultsMessage={translates.noResult}
                        onChange={(e, { value }) => setRequestType(value)}
                    />
                </div>
                <div className="requestItem">
                    <div>{translates.requestDate}<b>*</b>:</div>
                    <DatePicker
                        format="yyyy.MM.dd"
                        tileDisabled={isTileDisabled}
                        value={values.requestDate}
                        disabled={sending}
                        onChange={value => setValues({ ...values, requestDate: value })}
                        minDate={new Date()} />
                </div>
                <div className="requestItem">
                    <div>{translates.requestWieght}, {translates.tonsShort}<b>*</b>:</div>
                    <Input
                        fluid
                        options={requestTypeOptions}
                        disabled={sending}
                        value={values.requestWieght}
                        onChange={(e, { value }) => setValues({ ...values, requestWieght: value })}
                    />
                </div>
                <div className="requestItem">
                    <Button onClick={sendRequest} disabled={sending} loading={sending}>{translates.sendRequest}</Button>
                </div>
            </div>
    )
}

export default injectIntl(BidPage)